import { useEffect, useState } from 'react';
import { Text } from '@querodelivery/uni-react';
import { MONITOR_REFETCH_INTERVAL } from '../../constants';
import styles from './refresh-countdown.module.scss';

const ONE_SECOND = 1000;
const TOTAL_SECONDS = MONITOR_REFETCH_INTERVAL / ONE_SECOND;
const RING_SIZE = 20;
const RING_RADIUS = 7;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface RefreshCountdownProps {
  lastUpdatedAt: number;
  isRefreshing: boolean;
}

const getRemainingSeconds = (lastUpdatedAt: number) =>
  Math.max(
    0,
    Math.ceil(
      (lastUpdatedAt + MONITOR_REFETCH_INTERVAL - Date.now()) / ONE_SECOND,
    ),
  );

export function RefreshCountdown({
  lastUpdatedAt,
  isRefreshing,
}: RefreshCountdownProps) {
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    getRemainingSeconds(lastUpdatedAt),
  );

  useEffect(() => {
    setRemainingSeconds(getRemainingSeconds(lastUpdatedAt));

    const intervalId = window.setInterval(
      () => setRemainingSeconds(getRemainingSeconds(lastUpdatedAt)),
      ONE_SECOND,
    );

    return () => window.clearInterval(intervalId);
  }, [lastUpdatedAt]);

  const isUpdating = isRefreshing || remainingSeconds === 0;
  const remainingProgress = isUpdating ? 0 : remainingSeconds / TOTAL_SECONDS;

  return (
    <div className={styles.chip} role="status">
      <svg
        className={styles.ring}
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        aria-hidden
      >
        <circle
          className={styles.ringTrack}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
        />
        <circle
          className={styles.ringProgress}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeDasharray={RING_CIRCUMFERENCE}
          // Offset negativo: o arco esvazia a partir do topo, no sentido horário
          strokeDashoffset={-RING_CIRCUMFERENCE * (1 - remainingProgress)}
        />
      </svg>
      <Text
        as="span"
        fontSize="sm"
        fontWeight="medium"
        lineHeight="sm"
        tone="base"
        numeric
      >
        {isUpdating ? 'Atualizando…' : `Atualiza em ${remainingSeconds}s`}
      </Text>
    </div>
  );
}
